import * as THREE from "three/webgpu";
import {SketchRelationContainer} from '../../sketch/solver/constraint/SketchRelationContainer';
import {SketchEntityKind} from '../../sketch/solver/constraint/relation-compatibility/SketchEntityKinds';
import {SketchRelationKind} from '../../sketch/solver/constraint/relation-compatibility/SketchRelationKinds';
import type {ISketchObject} from './ISketchObject';
import type {SketchPointObject} from './SketchPointObject';
import type {SketchContour} from '../meshProxy/SketchContour';
import {SketchSnapshot} from '../meshProxy/SketchSnapshot';
import {
    isDashDotLIneNodeMaterial,
    LineMaterialFactory,
    LineMaterialState,
    SketchEdgeBaseStyle,
    SketchLineMaterial,
    SketchLineMaterialDepthPolicy,
    ThinChainLIneNodeMaterial,
} from '../../material';
import type {ReferencePlane} from '../../reference/ReferencePlane';
import type {SketchCloneContext} from './SketchCloneContext';
import {cloneSingleTargetConstraints} from './SketchConstraintClone';
import {createSketchEntityPersistentId} from '../../sketch/identity/SketchPersistentIdFactory';
import {markOwnerSketchGroupDirty} from './utils/markOwnerSketchGroupDirty';
import {createLineDistancePercentages} from '../../material/utils/createLineDistancePercentages';
import {scheduleDisposableResourceDisposal} from '../../utils';
import type {SketchEdgeGeometryRefreshOptions} from '../../../types/mesh/sketch/SketchEdgeGeometryRefreshTypes';

export {isAbstractSketchEdgeObject} from '../guards/isAbstractSketchEdgeObject';

/**
 * {@link AbstractSketchEdgeObject} 通用构造参数。
 * 线类实体（线段、圆等）共享同一参数协议。
 */
export interface AbstractSketchEdgeObjectOptions {
    /** 可选渲染材质。 */
    material?: SketchLineMaterial;
    /** 是否为构造线（默认 false）。 */
    isConstruction?: boolean;
    /** 基础线型状态。 */
    baseStyle?: SketchEdgeBaseStyle;
}

/**
 * 从位置 attribute 提取折线点列。
 * @param positionAttribute 位置 attribute。
 * @returns 折线点列。
 */
function createLinePointsFromPositionAttribute(
    positionAttribute: THREE.BufferAttribute | THREE.InterleavedBufferAttribute,
): THREE.Vector3[] {
    const points: THREE.Vector3[] = [];

    for (let index = 0; index < positionAttribute.count; index += 1) {
        points.push(new THREE.Vector3().fromBufferAttribute(positionAttribute, index));
    }

    return points;
}

/**
 * 克隆点线点混合虚线 Node 材质。
 * @param sourceMaterial 原始材质。
 * @returns 独立的新材质。
 */
function cloneDashDotLineNodeMaterial(sourceMaterial: ThinChainLIneNodeMaterial): ThinChainLIneNodeMaterial {
    const clonedMaterial = new ThinChainLIneNodeMaterial({
        color: Reflect.get(sourceMaterial, 'color') as THREE.ColorRepresentation | undefined,
        linewidth: Reflect.get(sourceMaterial, 'linewidth') as number | undefined,
        dashSize: sourceMaterial.dashSize,
        gapSize: sourceMaterial.gapSize,
        dotSize: sourceMaterial.dotSize,
        scale: sourceMaterial.scale,
        opacity: Reflect.get(sourceMaterial, 'opacity') as number | undefined,
        transparent: Reflect.get(sourceMaterial, 'transparent') as boolean | undefined,
    });

    clonedMaterial.points = sourceMaterial.points;
    Reflect.set(clonedMaterial, 'name', Reflect.get(sourceMaterial, 'name'));

    return SketchLineMaterialDepthPolicy.apply(clonedMaterial);

}

/**
 * 可为边对象创建闭合轮廓的宿主协议。
 *
 * 设计说明：
 * 1. 线段、圆弧等单条边本身不是 profile，真正的闭合轮廓由所属闭合集合或槽口宿主创建；
 * 2. 这里使用结构化协议避免抽象基类反向依赖具体集合类；
 * 3. 子类只需传入自己的闭合宿主，即可复用同一条委托链路。
 */
interface SketchContourHost {
    /** 获取或懒创建当前宿主持有的轮廓。 */
    getContour?: () => SketchContour | null;
    /** 创建当前宿主的轮廓。 */
    createContour?: () => SketchContour | null;
    /** 刷新当前宿主持有的轮廓派生几何。 */
    refreshContourGeometry?: () => void;
}

/**
 * 草图线状实体抽象基类。
 * 职责：
 * 1. 管理约束求解防重入保护；
 * 2. 管理基础线型状态；
 * 3. 管理轮廓引用与拖拽留影；
 * 4. 委托子类完成端点访问与几何刷新的具体实现。
 */
export abstract class AbstractSketchEdgeObject extends THREE.Line implements ISketchObject {
    /** 对象运行时类型标记（子类提供）。 */
    public abstract readonly objectType: SketchEntityKind;
    public readonly isSketchObject: boolean = true;
    /** 默认线状实体不是封闭几何，闭合实体由具体子类覆写。 */
    public get isClosedGeometry(): boolean {
        return false;
    }
    isAbstractSketchEdgeObject = true
    /** 跨会话稳定持久 ID；`cloneSelf` 继承，复制出新实体的流程必须重新生成。 */
    public persistentId: string = createSketchEntityPersistentId();
    /**
     * 当前对象的约束容器（子类提供，泛型参数绑定到各自具体类型）。
     */
    public readonly constraints: SketchRelationContainer = new SketchRelationContainer(this);
    /**
     * 转换实体引用的派生诊断显示状态。
     *
     * 该状态不参与持久化和 Undo/Redo；引用诊断投影器会根据仓库记录随时重建。
     */
    private referenceDiagnosticStateValue: 'normal' | 'dangling' | 'failed' = 'normal';

    /** @returns 当前引用诊断显示状态。 */
    public get referenceDiagnosticState(): 'normal' | 'dangling' | 'failed' {
        return this.referenceDiagnosticStateValue;
    }

    /**
     * 更新引用诊断显示状态并同步默认材质。
     *
     * @param state 由引用记录派生的目标状态。
     */
    public setReferenceDiagnosticState(state: 'normal' | 'dangling' | 'failed'): void {
        if (this.referenceDiagnosticStateValue === state) {
            return;
        }
        this.referenceDiagnosticStateValue = state;
        this.material = state === 'failed'
            ? LineMaterialFactory.getReferenceFailedMaterial(LineMaterialState.Default)
            : state === 'dangling'
                ? LineMaterialFactory.getReferenceDanglingMaterial(LineMaterialState.Default)
                : LineMaterialFactory.getLineMaterialByBaseStyle(
                    this._baseStyle,
                    LineMaterialState.Default,
                );
        if (this.shouldSyncLineDistancesForMaterial(this.material)) {
            this.computeLineDistances();
        }
    }


    /** 基础线型状态内部存储。 */
    private _baseStyle: SketchEdgeBaseStyle = SketchEdgeBaseStyle.Basic;


    /**
     * 获取是否为构造线。
     * @returns 当前构造线标记。
     */
    public get isConstruction(): boolean {
        return this._baseStyle !== SketchEdgeBaseStyle.Basic;
    }

    /**
     * 设置是否为构造线。
     * 副作用：根据绘制流程切换材质（构造线使用虚线材质，普通线使用实线材质），
     * 并重新计算虚线距离。
     * @param value 新的构造线标记。
     */
    public set isConstruction(value: boolean) {
        this.baseStyle = value
            ? SketchEdgeBaseStyle.Construction
            : SketchEdgeBaseStyle.Basic;
    }

    /**
     * 获取基础线型状态。
     * @returns 当前基础线型状态。
     */
    public get baseStyle(): SketchEdgeBaseStyle {
        return this._baseStyle;
    }

    /**
     * 设置基础线型状态。
     * 说明：
     * 1. 该状态描述对象默认视觉基础；
     * 2. 交互态材质必须在该基础样式之上派生；
     * 3. 切换基础样式时仅更新默认材质，不直接决定 Hover / Selected。
     * @param value 新的基础线型状态。
     */
    public set baseStyle(value: SketchEdgeBaseStyle) {
        if (this._baseStyle === value) {
            return;
        }

        this._baseStyle = value;
        this.material = this.referenceDiagnosticStateValue === 'failed'
            ? LineMaterialFactory.getReferenceFailedMaterial(LineMaterialState.Default)
            : this.referenceDiagnosticStateValue === 'dangling'
                ? LineMaterialFactory.getReferenceDanglingMaterial(LineMaterialState.Default)
                : LineMaterialFactory.getLineMaterialByBaseStyle(
                    value,
                    LineMaterialState.Default,
                );

        if (this.shouldSyncLineDistancesForMaterial(this.material)) {
            this.computeLineDistances();
        }
    }

    /**
     * 重新计算线距离并同步构造线附加 attribute。
     * 说明：
     * 1. 先复用 three 原生实现生成 `lineDistance`；
     * 2. 再同步 `lineDistancePercent`，供 Node 构造线材质按屏幕空间节奏着色；
     * 3. 若当前材质为点线点混合虚线 Node 材质，还会把 geometry 点列同步到材质 `points`。
     * @returns 当前对象本身。
     */
    public override computeLineDistances(): this {
        const result = super.computeLineDistances();
        const geometry = this.geometry;

        if (geometry.index !== null) {
            return result;
        }

        const positionAttribute = geometry.getAttribute('position');
        if (!positionAttribute) {
            return result;
        }

        const lineDistanceAttribute = geometry.getAttribute('lineDistance');
        if (!lineDistanceAttribute) {
            geometry.deleteAttribute('lineDistancePercent');
            return result;
        }

        const lineDistancePercentages = createLineDistancePercentages(lineDistanceAttribute);
        geometry.setAttribute(
            "lineDistancePercent",
            new THREE.Float32BufferAttribute(lineDistancePercentages, 1),
        );

        const targetMaterial = Array.isArray(this.material)
            ? this.material[0]
            : this.material;
        if (isDashDotLIneNodeMaterial(targetMaterial)) {
            targetMaterial.points = createLinePointsFromPositionAttribute(positionAttribute);
        }

        return result;
    }

    /** 当前边对象关联的轮廓对象。 */
    public contour: SketchContour | null = null;

    /** 与当前线状实体相关联的草图点集合。 */
    public readonly relatedSketchPoints = new Set<SketchPointObject>();


    /** 当前边对象拖动留影代理。 */
    public readonly dragSnapshotProxy: SketchSnapshot = new SketchSnapshot();

    /**
     * 当前曲线拖拽会话中已经实际启动留影的关联点。
     *
     * 设计说明：
     * 1. 圆的半径定义点等内部计算点会注册为关联点，但不一定挂载到场景；
     * 2. 点对象会自行决定是否创建视觉快照，但所有关联点都必须进入对称的拖拽生命周期；
     * 3. 结束阶段必须只清理本次实际启动过的点，避免成员变化形成不对称生命周期。
     */
    private readonly activeDragPreviewPoints = new Set<SketchPointObject>();


    /** 对象显示名称前缀（子类提供）。 */
    public abstract get baseDisplayName(): string;

    /** 线状实体所属参考面（子类提供）。 */
    public abstract get referencePlane(): ReferencePlane;

    /**
     * 复制当前线状实体，返回独立副本。
     * @param context 复制上下文。
     * @returns 当前对象的独立副本。
     */
    public abstract cloneSelf(context?: SketchCloneContext): AbstractSketchEdgeObject;

    /**
     * 构造草图线状实体基础部分。
     * @param options 构造参数。
     */
    protected constructor(options: AbstractSketchEdgeObjectOptions) {
        super();
        // 构造函数中直接赋值内部字段，跳过 setter 副作用（此时几何尚未就绪）。
        this._baseStyle = options.baseStyle
            ?? (options.isConstruction ? SketchEdgeBaseStyle.Construction : SketchEdgeBaseStyle.Basic);
        if (options.material) {

            // 外部注入材质也必须遵守草图前景策略，避免绕过统一材质工厂后重新启用深度。
            this.material = SketchLineMaterialDepthPolicy.apply(options.material);

        } else {
            this.material = LineMaterialFactory.getLineMaterialByBaseStyle(
                this._baseStyle,
                LineMaterialState.Default,
            );
        }
    }

    /**
     * 判断当前材质是否需要同步线距离。
     * @param material 待判断材质。
     * @returns 当材质依赖 lineDistance 或 lineDistancePercent 时返回 true。
     */
    protected shouldSyncLineDistancesForMaterial(material: SketchLineMaterial | THREE.Material | THREE.Material[]): boolean {
        const targetMaterial = Array.isArray(material)
            ? material[0]
            : material;

        return targetMaterial instanceof THREE.LineDashedMaterial
            || isDashDotLIneNodeMaterial(targetMaterial);
    }

    // ─── 子类必须实现的协议 ───────────────────────────────────────────────────
    /**
     * 按当前起 / 终点位置重建 THREE.js 几何数据。
     * 子类在此方法内刷新 BufferGeometry 顶点并处理虚线距离等额外逻辑。
     * @param options 派生几何刷新选项。
     */
    protected abstract updateGeometry(options?: SketchEdgeGeometryRefreshOptions): void;

    /**
     * 按刷新选项请求复合宿主同步轮廓。
     *
     * 设计说明：
     * 1. 独立修改边对象时默认立即刷新，保持既有修剪、属性编辑等调用语义；
     * 2. 求解器批量写回时只刷新边自身，宿主轮廓由批次末尾统一去重刷新；
     * 3. 该方法只控制复合宿主，自闭合圆、椭圆和样条自身的轮廓不受影响。
     *
     * @param host 当前边所属的复合轮廓宿主。
     * @param options 本次边几何刷新选项。
     */
    protected requestCompositeHostContourRefresh(
        host: SketchContourHost | null | undefined,
        options?: SketchEdgeGeometryRefreshOptions,
    ): void {
        if (!host || options?.compositeVisualRefreshMode === 'deferred') {
            return;
        }
        host.refreshContourGeometry?.();
    }

    /**
     * 判断当前边对象在当前几何状态下是否具备创建轮廓的能力。
     * @returns 若可创建轮廓返回 true，否则返回 false。
     */
    public abstract canCreateContour(): boolean;

    /**
     * 基于当前边对象创建轮廓对象。
     * @returns 创建得到的轮廓对象。
     */
    public abstract createContour(): SketchContour;

    /**
     * 从闭合宿主恢复当前边所属的轮廓。
     *
     * 设计说明：
     * 1. 退出草图后会移除上色轮廓的场景显示，但边到闭合宿主的拓扑关系不能断；
     * 2. 拉伸、旋转等 profile 特征选中边时，必须沿“边 -> 闭合宿主 -> 轮廓”恢复数据；
     * 3. 该能力放在抽象边对象中，避免线段、圆弧等子类各自重复委托实现。
     *
     * @param host 当前边关联的闭合宿主。
     * @param ownerName 当前边类型名称，用于生成明确错误。
     * @returns 闭合宿主恢复出的轮廓。
     */
    protected createContourFromHost(host: SketchContourHost | null, ownerName: string): SketchContour {
        if (!host) {
            throw new Error(`${ownerName}未关联闭合轮廓宿主，无法创建轮廓。`);
        }

        const contourGetter = host.getContour;
        if (typeof contourGetter === 'function') {
            const contour = contourGetter.call(host);
            if (contour) {
                this.contour = contour;
                return contour;
            }
        }

        const contourCreator = host.createContour;
        if (typeof contourCreator === 'function') {
            const contour = contourCreator.call(host);
            if (contour) {
                this.contour = contour;
                return contour;
            }
        }

        throw new Error(`${ownerName}关联的闭合轮廓宿主无法创建轮廓。`);
    }

    /**
     * 清理当前边对象上的轮廓引用。
     */
    public clearContour(): void {
        this.contour = null;
    }

    /**
     * 注册一个与当前线状实体相关联的草图点。
     * @param point 草图点对象。
     */
    protected registerRelatedSketchPoint(point: SketchPointObject): void {
        this.relatedSketchPoints.add(point);
        point.registerRelatedSketchObject(this);
    }

    /**
     * 解除一个与当前线状实体相关联的草图点。
     * @param point 草图点对象。
     */
    protected unregisterRelatedSketchPoint(point: SketchPointObject): void {
        this.relatedSketchPoints.delete(point);
        point.unregisterRelatedSketchObject(this);
    }

    /**
     * 批量注册与当前线状实体相关联的草图点。
     * @param points 草图点对象列表。
     */
    protected registerRelatedSketchPoints(points: SketchPointObject[]): void {
        points.forEach((point) => {
            this.registerRelatedSketchPoint(point);
        });
    }

    /**
     * 批量解除与当前线状实体相关联的草图点。
     * @param points 草图点对象列表。
     */
    protected unregisterRelatedSketchPoints(points: SketchPointObject[]): void {
        points.forEach((point) => {
            this.unregisterRelatedSketchPoint(point);
        });
    }

    /**
     * 获取当前关联草图点的只读快照。
     * @returns 草图点列表副本。
     */
    public getRelatedSketchPointsSnapshot(): readonly SketchPointObject[] {
        return [...this.relatedSketchPoints];
    }

    /**
     * 刷新当前实体关联点连接的其它边几何。
     *
     * 设计说明：
     * 1. 共享点被静默写回时不会主动通知连接边，调用方需要在批量点位更新完成后统一收口；
     * 2. 当前实体已由调用方完成定义刷新，必须排除自身，避免圆弧等参数实体被二次反推定义；
     * 3. 这里按边对象去重，避免一条边同时连接多个已更新点时重复刷新；
     * 4. 只刷新几何与轮廓派生显示，不触发约束求解，避免重建流程递归进入求解器。
     */
    public refreshConnectedNeighborGeometries(): void {
        const affectedEdges = new Set<AbstractSketchEdgeObject>();
        this.relatedSketchPoints.forEach((point) => {
            point.getConnectedEdgesSnapshot().forEach((edge) => {
                if (edge !== this) {
                    affectedEdges.add(edge);
                }
            });
        });
        affectedEdges.forEach((edge) => edge.refreshGeometryOnly());
    }


    /**
     * 拖动开始时显示完整草图曲线及关联控制点的原始位置留影。
     *
     * 设计说明：
     * 1. SolidWorks 的“拖动时的幻影图象”是草图实体级通用行为，不应由直线、圆、圆弧、
     *    椭圆或抛物线分别重复实现；
     * 2. 拖动任一控制点会改变整条实体，因此留影必须捕获拖动开始前的完整曲线，而不是只保留被拖点；
     * 3. 关联点按 UUID 去重，避免共享点或复合控制点重复进入留影深度；
     * 4. 留影只属于交互预览，不参与选择、捕捉、约束、持久化或撤销重做。
     */
    public beginDragPreview(): void {
        const visitedPointIds = new Set<string>();
        this.activeDragPreviewPoints.clear();
        this.relatedSketchPoints.forEach((point) => {
            if (visitedPointIds.has(point.uuid)) {
                return;
            }
            visitedPointIds.add(point.uuid);

            // 点对象自身统一区分“视觉快照”和“无视觉生命周期”，边对象只负责对称启动。
            point.beginDragPreview();
            this.activeDragPreviewPoints.add(point);
        });

        // 点留影就绪后再捕获完整曲线，确保本次拖拽只保留一份起始几何快照。
        this.dragSnapshotProxy.show(this);
    }

    /**
     * 拖动结束时隐藏完整曲线与关联控制点留影。
     *
     * 结束流程必须与 {@link beginDragPreview} 对称：先移除曲线快照，再逐点退出留影态，
     * 使样条等依赖点级拖拽生命周期的对象能在最后一个点退出时统一执行最终几何收口。
     */
    public endDragPreview(): void {
        this.dragSnapshotProxy.hide();

        this.activeDragPreviewPoints.forEach((point) => {
            point.endDragPreview();
        });
        this.activeDragPreviewPoints.clear();
    }

    /**
     * 关联点进入拖拽留影态时的通知钩子。
     *
     * 设计说明：
     * 1. 点拖拽、轮廓拖拽、集合拖拽都会先进入点级留影态；
     * 2. 某些线状实体需要利用这一生命周期暂停交互期的重几何计算；
     * 3. 抽象基类默认不做任何处理，由具体子类按需覆写。
     */
    public onRelatedPointDragPreviewBegan(): void {
    }

    /**
     * 关联点退出拖拽留影态时的通知钩子。
     *
     * 设计说明：
     * 1. 用于在最后一个关联点结束拖拽后执行几何收口；
     * 2. 默认实现为空，避免影响已有同步几何对象；
     * 3. 需要拖拽结束后补算的子类可在此统一收口。
     */
    public onRelatedPointDragPreviewEnded(): void {
    }


    // ─── 通用实现 ─────────────────────────────────────────────────────────────

    /**
     * 按当前端点位置同步几何。
     *
     * 该方法只负责派生几何刷新；关系、尺寸与操作目标必须由外层统一参数协调器求解，
     * 禁止端点通知链在对象内部隐式启动第二套约束传播。
     *
     * @param movedPoint 触发本次派生刷新的控制点。
     */
    public syncEndpointsToGeometry(movedPoint?: SketchPointObject): void {
        void movedPoint;
        this.updateGeometry();
    }

    /**
     * 判断当前边对象是否被固定。
     * @returns 当存在 Fix 约束时返回 true。
     */
    public isFixed(): boolean {
        return this.constraints.hasType(SketchRelationKind.Fix);
    }


    /**
     * 仅刷新当前边几何，不触发约束求解。
     * 说明：
     * 1. 用于事务回滚后同步渲染几何；
     * 2. 不会进入约束容器执行链路。
     * @param movedPoint 当前拖拽移动的点对象。
     * @param options 派生几何刷新选项。
     */
    public refreshGeometryOnly(
        movedPoint?: SketchPointObject,
        options?: SketchEdgeGeometryRefreshOptions,
    ): void {
        void movedPoint;
        this.updateGeometry(options);
    }


    /**
     * 判断当前线状实体是否允许拖动。
     * 说明：
     * 1. 抽象基类只负责检查对象自身是否存在固定约束；
     * 2. 具体子类若还依赖端点/控制点固定态，应自行覆写扩展；
     * 3. 只要对象自身存在 `Fix` 约束，就视为不可拖动。
     * @returns 允许拖动返回 true，否则返回 false。
     */
    public canDrag(): boolean {
        return !this.isFixed();
    }

    /**
     * 标记所属草图组已进入 dirty 状态。
     * 说明：
     * 1. 该方法服务于圆、椭圆、圆弧等会直接修改自身定义的边对象；
     * 2. 只负责底层“定义已变更”上报，不直接触发特征重建；
     * 3. committed 由拖拽结束、退出草图、缩放结束等上层提交流程统一控制。
     * @param reason 变更原因。
     */
    protected markOwnerSketchGroupDirty(reason: string): void {
        markOwnerSketchGroupDirty(this, {
            reason,
            sourceObjectId: this.uuid,
        });
    }


    /**
     * 通过两个端点构建两顶点线几何（静态工厂）。
     * @param startPoint 起点对象。
     * @param endPoint 终点对象。
     * @returns 初始化完成的 BufferGeometry。
     */
    protected static createTwoPointGeometry(
        startPoint: SketchPointObject,
        endPoint: SketchPointObject,
    ): THREE.BufferGeometry {
        const start = startPoint.getPosition();
        const end = endPoint.getPosition();
        const geometry = new THREE.BufferGeometry();
        const attribute = new THREE.Float32BufferAttribute(
            [start.x, start.y, start.z, end.x, end.y, end.z],
            3,
        );
        geometry.setAttribute('position', attribute);
        geometry.computeBoundingBox();
        geometry.computeBoundingSphere();
        return geometry;
    }

    /**
     * 将曲线采样点同步到当前线几何，并保持同拓扑顶点缓冲的资源身份稳定。
     *
     * 设计说明：
     * 1. WebGPU 渲染管线会缓存线对象的顶点缓冲绑定，同一渲染周期内替换并释放几何体可能导致 slot 0 绑定失效；
     * 2. 采样点数量相同时只原位重写既有 `position`，避免创建新的 GPU 顶点缓冲；
     * 3. 只有采样点数量变化时才替换几何体，并通过延迟释放队列回收旧资源；
     * 4. 返回旧几何是否已有射线加速结构，由具体曲线决定重建或校正 BVH。
     *
     * @param worldPoints 已转换到世界坐标系的曲线采样点。
     * @returns 是否发生了需要重新建立射线加速结构的几何体替换。
     */
    protected syncSampledLineGeometry(worldPoints: THREE.Vector3[]): boolean {
        const position = this.geometry.getAttribute('position');

        if (
            position instanceof THREE.BufferAttribute
            && position.count === worldPoints.length
        ) {
            // 同拓扑更新只写入坐标，保留 BufferGeometry 与 BufferAttribute 的 GPU 资源身份。
            for (let index = 0; index < worldPoints.length; index += 1) {
                const point = worldPoints[index];
                position.setXYZ(index, point.x, point.y, point.z);
            }
            position.needsUpdate = true;
            return false;
        }

        if (!position) {
            // 新对象的初始空几何可直接建立顶点属性，不产生无意义的待释放资源。
            this.geometry.setFromPoints(worldPoints);
            return false;
        }

        // 采样拓扑变化时必须创建匹配长度的新缓冲，并在安全帧延迟释放旧几何。
        const previousGeometry = this.geometry;
        const hadRaycastAcceleration = Boolean(
            Reflect.get(previousGeometry, 'boundsTree'),
        );
        this.geometry = new THREE.BufferGeometry().setFromPoints(worldPoints);
        scheduleDisposableResourceDisposal(previousGeometry);
        return hadRaycastAcceleration;
    }


    /**
     * 复制线状实体基础构造参数。
     * 说明：
     * 1. 返回值中的材质为新实例，避免与源对象共享可变材质状态；
     * 2. 基础线型状态由 `baseStyle` 显式传递，保证复制后视觉语义一致。
     * @returns 线状实体基础构造参数副本。
     */
    protected createCloneBaseOptions(): AbstractSketchEdgeObjectOptions {
        return {
            material: this.cloneLineMaterialForCopy(),
            baseStyle: this.baseStyle,
        };
    }

    /**
     * 将当前对象通用显示状态同步到副本对象。
     * @param target 目标副本对象。
     */
    protected syncCommonDisplayStateToClone(target: THREE.Object3D): void {
        target.position.copy(this.position);
        target.quaternion.copy(this.quaternion);
        target.scale.copy(this.scale);
        target.visible = this.visible;
        target.renderOrder = this.renderOrder;
        target.frustumCulled = this.frustumCulled;
        target.layers.mask = this.layers.mask;
    }


    /**
     * 将当前对象上的单目标几何关系复制到副本对象。
     * @param target 目标副本对象。
     * @param context 当前复制上下文，用于执行调用方的关系准入策略。
     */
    protected cloneSingleTargetConstraintsTo(
        target: AbstractSketchEdgeObject,
        context?: SketchCloneContext,
    ): void {
        cloneSingleTargetConstraints(this, target, context?.shouldCloneConstraint);
    }


    /**
     * 在复制上下文中注册对象映射。
     * @param context 复制上下文。
     * @param clonedObject 目标副本对象。
     */
    protected registerObjectClone(
        context: SketchCloneContext | undefined,
        clonedObject: AbstractSketchEdgeObject,
    ): void {
        // 副本表达同一逻辑边实体，必须继承持久身份；复制出新实体的流程负责显式重新生成。
        clonedObject.persistentId = this.persistentId;
        if (!context) {
            return;
        }
        context.objectMap.set(this.uuid, clonedObject);
    }


    /**
     * 复制当前线材质。
     * @returns 复制后的线材质。
     */
    private cloneLineMaterialForCopy(): SketchLineMaterial {
        const sourceMaterial = Array.isArray(this.material)
            ? this.material[0]
            : this.material;

        if (isDashDotLIneNodeMaterial(sourceMaterial)) {
            return cloneDashDotLineNodeMaterial(sourceMaterial);
        }

        if (sourceMaterial instanceof THREE.LineBasicMaterial || sourceMaterial instanceof THREE.LineDashedMaterial) {

            return SketchLineMaterialDepthPolicy.apply(sourceMaterial.clone());

        }

        throw new Error('当前线对象材质类型非法，无法复制。');
    }

}
